import { useMutation, UseMutationResult, useQueryClient, useSuspenseQuery } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { CreatePushDeviceRequest, PushDeviceResponse } from '../generated-types';
import { getPushDevice, createPushDevice, deactivatePushDevice } from '../services/pushNotificationService';

const QUERY_KEY = 'pushdevice';
export const usePushDevice = (enabled: boolean) =>
    useSuspenseQuery<PushDeviceResponse, RequestExceptionWithResponse>({
        queryKey: [QUERY_KEY],
        queryFn: () => {
            if (!enabled) return Promise.resolve({ enabled: false });
            return getPushDevice();
        },
    });

export const useCreatePushDevice = (): UseMutationResult<
    unknown,
    RequestExceptionWithResponse,
    CreatePushDeviceRequest
> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [QUERY_KEY],
        mutationFn: createPushDevice,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
            const previousState = queryClient.getQueryData<PushDeviceResponse>([QUERY_KEY]);
            queryClient.setQueryData([QUERY_KEY], () => ({
                enabled: true,
            }));
            return { previousState };
        },
        onError: (_error, _data, context) => {
            queryClient.setQueryData([QUERY_KEY], context?.previousState);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
};

export const useDeactivatePushDevice = (): UseMutationResult<unknown, RequestExceptionWithResponse, void> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [QUERY_KEY],
        mutationFn: deactivatePushDevice,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
            const previousState = queryClient.getQueryData<PushDeviceResponse>([QUERY_KEY]);
            queryClient.setQueryData([QUERY_KEY], () => ({
                enabled: false,
            }));
            return { previousState };
        },
        onError: (_error, _data, context) => {
            queryClient.setQueryData([QUERY_KEY], context?.previousState);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
};
